import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps } from 'antd';
import { FC, useMemo, useState } from 'react';
import { generateOptions, Option } from '@utils/generateOptions';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CloseOutlined } from '@ant-design/icons';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SelectedItems } from '@components/SelectedItems/SelectedItems';
import { Select } from '@dynamika-shared/ui/Select/Select';
import {
  RequestSiteConfigurationType,
  SiteConfigurationType,
} from '@dynamika-shared/types/site';
import { SearchIcon } from '@dynamika-shared/ui/icons';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import { SiteMode } from '../ClinicalTrialSites.view';
import { schema } from './schema';
import { OptionRenderer } from '@components/OptionRenderer/OptionRenderer';
import { useFacility } from './hooks/useFacility';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { HiddenTag } from '@components/SelectLabels/SelectLabelsOptionRender';
import { FlattenOptionData } from 'rc-select/lib/interface';
import { BaseOptionType } from 'rc-select/lib/Select';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useParams } from 'react-router-dom';

type SiteFormDrawerProps = DrawerProps & {
  close?: () => void;
  mode: SiteMode;
  currentSite?: SiteConfigurationType;
};

export const SiteFormDrawer: FC<SiteFormDrawerProps> = ({
  close,
  mode,
  currentSite,
}) => {
  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();

  const [loading, createSite, updateSite] = useClinicalTrialSlice(
    (state, actions) => [
      state.clinicalTrialActionLoading,
      actions.createSiteRequest,
      actions.updateSiteRequest,
    ]
  );
  const facilityOptions = useMemo(() => {
    return generateOptions(currentSite?.imagingFacilities);
  }, [currentSite?.imagingFacilities]);

  const [selectedFacilities, setSelectedFacilities] =
    useState<Option[]>(facilityOptions);
  const [searchValue, setSearchValue] = useState<string>('');
  const [askQuit, setAskQuit] = useState<boolean>(false);

  const { watch, getValues, setValue, formState, trigger } =
    useForm<RequestSiteConfigurationType>({
      resolver: yupResolver(schema),
      defaultValues: {
        id: currentSite?.id,
        code: currentSite?.code,
        principalInvestigator: currentSite?.principalInvestigator,
        imagingFacilities: currentSite?.imagingFacilities.map(f => f.id) ?? [],
      },
    });

  const { errors, isValid, isDirty, touchedFields } = formState;

  const [memoizedFacilities, delayedSearch] = useFacility();

  const disabled = useMemo(() => {
    return selectedFacilities.length === 0 || loading || !isValid || !isDirty;
  }, [selectedFacilities, loading, isValid, isDirty]);

  const onChangeItems = (items: Option[]) => {
    const newItems = items.map(label => label.value);
    setValue('imagingFacilities', newItems, {
      shouldDirty: true,
      shouldTouch: true,
    });
    trigger('imagingFacilities');
  };
  const handleTagClose = (removedItem: Option) => {
    const updatedItems = selectedFacilities.filter(
      item => item !== removedItem
    );
    setSelectedFacilities(updatedItems);
    onChangeItems(updatedItems);
  };

  const onCloseDrawer = () => {
    if (mode === 'SITE_CREATE') {
      if (selectedFacilities.length === 0 && !isDirty) {
        close && close();
      } else {
        setAskQuit(true);
      }
    } else {
      const hasTouchedFields = Object.keys(touchedFields).length;
      if (hasTouchedFields) {
        setAskQuit(true);
      } else {
        close && close();
      }
    }
  };

  const onChange = (
    name: keyof RequestSiteConfigurationType,
    value: string
  ) => {
    setValue(name, value, { shouldDirty: true, shouldTouch: true });
    trigger(name);
  };

  const onBlur = (
    fieldName: keyof RequestSiteConfigurationType,
    value: string
  ) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value.trim());
    }
  };

  const onSelect = (value: string) => {
    const selectedItem = memoizedFacilities.find(f => f.value === value);
    if (selectedItem) {
      setSelectedFacilities(prevState => {
        const updatedItems = [...prevState, selectedItem];
        onChangeItems(updatedItems);
        return [...prevState, selectedItem];
      });
    }
  };

  const onDeselect = (value: string) => {
    const indexToRemove = selectedFacilities.findIndex(
      item => item.value === value
    );
    if (indexToRemove !== -1) {
      setSelectedFacilities(prevState => {
        const updatedItems = [...prevState];
        updatedItems.splice(indexToRemove, 1);
        onChangeItems(updatedItems);
        return updatedItems;
      });
    }
  };

  const hiddenTagRender = () => {
    return <HiddenTag />;
  };

  const imagingFacilitiesOptionRender = (
    option: FlattenOptionData<BaseOptionType>
  ) => {
    return (
      <OptionRenderer
        searchValue={searchValue}
        option={option}
        selectedOptions={selectedFacilities}
      />
    );
  };

  const handleCreateSite = () => {
    if (clinicalTrialId) {
      createSite({ clinicalTrialId, values: getValues() });
    }
  };
  const handleUpdateSite = () => {
    if (clinicalTrialId && currentSite?.id) {
      updateSite({
        clinicalTrialId,
        siteId: currentSite.id,
        values: getValues(),
      });
    }
  };

  return (
    <Drawer
      data-testid='site-form-drawer'
      classNames={{
        header:
          '!px-0 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !pl-0 !pr-2',
        content: 'rounded-lg',
      }}
      title={
        currentSite ? (
          <div className='flex gap-1'>
            Update{' '}
            <CustomTooltip MAX_LABEL_LENGTH={10} label={currentSite?.code} />{' '}
            Site
          </div>
        ) : (
          'Add Site'
        )
      }
      open
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined data-testid='close-drawer-button' />}
    >
      <div className='px-1'>
        <Input
          required
          value={watch('code')}
          name={'code'}
          onChange={e => onChange('code', e.target.value)}
          onBlur={e => onBlur('code', e.target.value)}
          data-testid={'site-code'}
          errors={errors.code?.message}
          label={'Site Code'}
          placeholder={'Site Code'}
          classNameInputWrapper='mb-3'
          maxLength={50}
        />
        <Input
          required
          value={watch('principalInvestigator')}
          name={'principalInvestigator'}
          onChange={e => onChange('principalInvestigator', e.target.value)}
          onBlur={e => onBlur('principalInvestigator', e.target.value)}
          data-testid={'site-principalInvestigator'}
          errors={errors.principalInvestigator?.message}
          label={'Principal Investigator'}
          placeholder={'Principal Investigator'}
          classNameInputWrapper='mb-3'
          maxLength={70}
        />
        <Select
          className='mb-2'
          label={'Imaging Facility'}
          mode='multiple'
          menuItemSelectedIcon={null}
          suffixIcon={<SearchIcon className='h-2 w-2' />}
          required
          options={memoizedFacilities}
          placeholder={'Imaging Facility'}
          tagRender={() => hiddenTagRender()}
          filterOption={false}
          searchValue={searchValue}
          notFoundContent={
            searchValue ? (
              <NoDataSelect
                text={searchValue}
                maxLength={10}
                name={'facilities'}
              />
            ) : null
          }
          onSearch={value => {
            setSearchValue(value);
            delayedSearch(value);
          }}
          value={selectedFacilities}
          showSearch
          listHeight={160}
          onBlur={() => setSearchValue('')}
          onSelect={onSelect}
          onDeselect={onDeselect}
          optionRender={option => imagingFacilitiesOptionRender(option)}
          name='imagingFacilities'
          data-testid='site-imagingFacilities'
        />
        <SelectedItems
          selectedItems={selectedFacilities}
          onClose={handleTagClose}
          title={'Selected Facility'}
        />
      </div>
      <footer className='mt-auto pr-2'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            data-testid='site-save-btn'
            className='w-[17rem]'
            type='primary'
            disabled={disabled}
            onClick={
              mode === 'SITE_CREATE' ? handleCreateSite : handleUpdateSite
            }
          >
            {mode === 'SITE_CREATE' ? 'Add' : 'Save'}
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </Drawer>
  );
};
